import {
  DeleteOutlined,
  LineChartOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Input, Tooltip } from '@mm/components';
import { useVisitedUrlEventTriggersCRUDActions } from '@mm/state/actions/VisitedUrlEventTriggersCRUDActions';
import { UpdateEventTrigger } from '@mm/state/controllers/VisitedUrlEventTriggersController';
import { KlaviyoEvents } from '@mm/state/model/KlaviyoModel';
import { VisitedUrlEventTrigger } from '@mm/state/model/VisitedUrlEventTriggerModel';
import { StoreState } from '@mm/state/store-interfaces';
import { Select, Switch } from 'antd';
import classNames from 'classnames';
import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { Help } from './KlaviyoHelpLink';
import styles from './KlaviyoIntegrations.module.css';

type Props = {
  controlsDisabled: boolean;
  tokenType: 'api_key' | 'oauth_token';
  tokenId: number;
  activeFbPageId: number;
  getEvents(facebookPageId: number, force: boolean): void;
};

const possibleMetrics = [
  'Website visit',
  'Collection view',
  'Product view',
  'Cart abandonment',
  'Checkout abandonment',
  'Return visit',
];

const camelizedMetricsMap = {
  'Website visit': 'websiteVisit',
  'Collection view': 'collectionView',
  'Product view': 'productView',
  'Cart abandonment': 'cartAbandonment',
  'Checkout abandonment': 'checkoutAbandonment',
  'Return visit': 'returnVisit',
};

type EventTriggerProps = {
  activeFbPageId: number;
  trigger: VisitedUrlEventTrigger;
  metrics: string[];
  update: (pageId: number, trigger: UpdateEventTrigger) => void;
  remove: (pageId: number, id: number) => void;
  eventsStats: KlaviyoEvents;
};

const EventTrigger = ({
  update,
  remove,
  trigger,
  metrics,
  activeFbPageId,
  eventsStats,
}: EventTriggerProps) => {
  const [pattern, setPattern] = useState(trigger.pattern);
  const eventsSent = eventsStats[camelizedMetricsMap[trigger.metric]] || 0;
  const debouncedUpdate = useRef(
    debounce(
      async (pattern: string, trigger: VisitedUrlEventTrigger) =>
        update(activeFbPageId, {
          id: trigger.id,
          pattern,
        }),
      1000,
    ),
  ).current;
  return (
    <>
      <div className={styles.gridColumn}>
        <Select
          className={styles.eventsSelect}
          placeholder="Choose"
          defaultValue={trigger.metric || undefined}
          onChange={(metric: string) =>
            update(activeFbPageId, {
              id: trigger.id,
              metric,
            })
          }
          options={metrics.map(metric => ({
            value: metric,
            label: metric,
          }))}
          popupClassName={styles.popup}
        />
      </div>
      <div className={styles.gridColumn}>
        {trigger.metric === 'Return visit' ? (
          <Input
            css={{
              container: styles.urlPatternContainer,
              input: styles.input,
            }}
            type="text"
            disabled={true}
            value="This event is triggered on returning visits"
          />
        ) : (
          <Input
            css={{
              container: styles.urlPatternContainer,
              input: styles.input,
            }}
            placeholder="Add URL keyword ex: /Cart"
            value={pattern}
            onChange={e => {
              const { value } = e.target;
              setPattern(value);
              debouncedUpdate(value, trigger);
            }}
          />
        )}
      </div>
      <div className={classNames(styles.gridColumn, styles.alignedColumn)}>
        <Switch
          defaultChecked={trigger.allContacts}
          checkedChildren="All"
          unCheckedChildren="Synced"
          className={styles.switch}
          onChange={ok =>
            update(activeFbPageId, { allContacts: ok, id: trigger.id })
          }
        />
      </div>
      <div className={classNames(styles.gridColumn, styles.alignedColumn)}>
        <LineChartOutlined /> {Intl.NumberFormat().format(eventsSent)}
      </div>
      <div className={classNames(styles.gridColumn, styles.alignedColumn)}>
        <DeleteOutlined onClick={() => remove(activeFbPageId, trigger.id)} />
      </div>
    </>
  );
};

export const KlaviyoEventTriggersSetup = ({
  controlsDisabled,
  tokenType,
  tokenId,
  activeFbPageId,
  getEvents,
}: Props) => {
  const eventTriggers = useSelector((state: StoreState) =>
    state.visitedUrlEventTriggers.visitedUrlEventTriggers.filter(
      trigger => trigger.integration === 'klaviyo',
    ),
  );
  const eventsStats = useSelector((state: StoreState) => state.klaviyo.events);
  const availableMetrics = possibleMetrics.filter(
    metric => !eventTriggers.find(trigger => trigger.metric === metric),
  );
  const canAdd = eventTriggers.length < possibleMetrics.length;
  const { create, update, remove } = useVisitedUrlEventTriggersCRUDActions();
  useEffect(() => {
    getEvents(activeFbPageId, false);
  }, []);
  return (
    <div
      className={classNames(styles.box, {
        [styles.boxDisabled]: controlsDisabled,
      })}
    >
      <div className={styles.gridContainer}>
        <div className={classNames(styles.boxWithHelp, styles.startAlligned)}>
          <div className={styles.boxContent}>
            <div
              className={classNames(
                styles.heading,
                styles.headingWithSwitch,
                styles.eventTriggersHeading,
              )}
            >
              <div>
                <b>Signal</b> - Superpower your flows with event data. Be sure
                to{' '}
                <a
                  className={styles.navLink}
                  href="https://customers.ai/help/l/en/article/ilu5nxh9mk-activate-signal"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  add the custom event to your flows.
                </a>
              </div>
            </div>
          </div>
          <Help
            link="https://customers.ai/help/l/en/article/ilu5nxh9mk-activate-signal"
            text="Learn more"
          />
        </div>
        {eventTriggers.length > 0 ? (
          <div className={styles.eventTriggersGrid}>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Event
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              URL Contains
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Sync Settings
              <Tooltip
                anchorElement={<QuestionCircleOutlined />}
                useHover
                css={{
                  container: styles.tooltipContainer,
                }}
                withoutLine
                position="top"
              >
                <div>
                  Choose which profiles you want to sync these events for. All
                  contacts available (recommended) OR restrict sync to just
                  audiences chosen in the integration sync above.
                </div>
              </Tooltip>
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Events Sent
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Actions
            </div>
            {eventTriggers.map(trigger => (
              <EventTrigger
                key={trigger.id}
                trigger={trigger}
                metrics={availableMetrics}
                eventsStats={eventsStats}
                activeFbPageId={activeFbPageId}
                update={update}
                remove={remove}
              />
            ))}
          </div>
        ) : null}
        {canAdd ? (
          <PrimaryButton
            className={classNames(
              'integrations__header-add-button-secondary',
              'integrations__header-add-button-action',
              'integrations__header-button-no-margin',
            )}
            onClick={() =>
              create(activeFbPageId, {
                tokenId,
                tokenType,
                integration: 'klaviyo',
              })
            }
            leftIcon={<PlusOutlined />}
          >
            Event
          </PrimaryButton>
        ) : null}
      </div>
    </div>
  );
};
